import CloseIcon from "@mui/icons-material/Close";
import FeedbackIcon from "@mui/icons-material/FeedbackOutlined";
import SendIcon from "@mui/icons-material/Send";
import { Box, Card, Divider, Grid, Tooltip as MuiTooltip, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { type TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip } from "chart.js";
import { debounce } from "lodash";
import { DateTime } from "luxon";
import { type Dispatch, memo, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { type HospitalDataContextType, useHospitalData } from "../../contexts/HospitalDataProvider";
import { useModalContext } from "../../contexts/ModalProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { DEMO_FONT_FAMILY, FONT_SIZE, HOSPITAL_DEMO, RADIUS } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import type { FeedbackValue } from "../../utils/models";
import axiosInstance from "../../utils/networkWrapper";
import { getDayTimeNumber } from "../../utils/utilFunctions";
import { Dot } from "../chartContainer/ChartContainer";
import { presentRangeScore } from "../patientDrawer/PatientDrawer";
import { recommendationBreakdown, type SidebarAreaSplits, sidebarAreaSplits } from "./recommendationBreakdown";
import { drawShiftCircleImsSrc } from "./utils";

ChartJS.register(CategoryScale, LinearScale, Title, Tooltip, Legend, PointElement, LineElement);
let feedbackIndex = -1;
type Props = {
	setDateWeekDayLabel: any;
	setDailyCensusLow: any;
	setDailyCensusHigh: any;
	setScore: any;
	setCensusColor: any;
	setScoreColor: any;
	shiftNo: number;
	setShiftNo: any;
	setAdmissionsLow: any;
	setAdmissionsHigh: any;
	setAdmissionsColor: any;
	setDischargesColor: any;
	setDischargesLow: any;
	setDischargesHigh: any;
	setIntermediateLow: any;
	setIntermediateHigh: any;
	drawerOpen: any;
	setDrawerOpen: any;
	setClickedShiftNo: any;
	setClickedDayNo: any;
	showFeedback: any;
	feedbackVals: any;
	setFeedbackVals: any;
	/** Per-area splits for the sidebar metrics, recomputed on hover. */
	setAreaSplits: Dispatch<SetStateAction<SidebarAreaSplits | null>>;
	/** Total RN recommendation for the hovered day (the graph node's number). */
	setRnRecommendation: Dispatch<SetStateAction<number>>;
};

const LineChart = ({
	setDateWeekDayLabel,
	setDailyCensusLow,
	setDailyCensusHigh,
	setScore,
	setCensusColor,
	setScoreColor,
	shiftNo,
	setShiftNo,
	setAdmissionsColor,
	setDischargesColor,
	setAdmissionsLow,
	setAdmissionsHigh,
	setDischargesLow,
	setDischargesHigh,
	setIntermediateLow,
	setIntermediateHigh,
	drawerOpen,
	setDrawerOpen,
	setClickedShiftNo,
	setClickedDayNo,
	showFeedback,
	feedbackVals,
	setFeedbackVals,
	setAreaSplits,
	setRnRecommendation,
}: Props) => {
	const { t, i18n } = useTranslation();
	const [hoveredIndex, setHoveredIndex] = useState(-1);
	const { hospitalData, chosenDepartment, getColorByDifficultyName, showErrorSnackbar, isHospitalView, isIcuTeleDemo }: HospitalDataContextType =
		useHospitalData();
	// Only hospital demos (Hospital type + inhouse2).
	const isDemoChart = isHospitalView;
	const { weekIndex } = useWeekIndex();
	const [daysData, setDaysData] = useState(hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days);
	const { sethoveredShiftNo, hoveredShiftNo } = useModalContext();
	const graphData = hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days.map((e: any) => e.staffNo);
	const maxVal = Math.max(...graphData);
	const [cursorStyle, setCursorStyle] = useState("default");
	const [tooltipData, setTooltipData] = useState<any>({
		visible: false,
		x: 0,
		y: 0,
		index: -1,
	});

	const { showSnackbar } = useSnackbar();
	const minVal = Math.min(...graphData);
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const divRefs: any = useRef([]);
	const detailsRefs: any = useRef([]);
	const chartRef: any = useRef(null);
	const feedbackRefs = useRef<Array<HTMLDivElement | null>>([]);
	const [feedbackCoords, setFeedbackCoords] = useState<any[]>([]);
	const [commentText, setCommentText] = useState("");

	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	useEffect(() => {
		const updateCoordinates = () => {
			setDivCoords(divRefs.current.map((div: any) => div?.getBoundingClientRect()));
			setDetailsCoords(detailsRefs.current.map((div: any) => div?.getBoundingClientRect()));
			setFeedbackCoords(feedbackRefs.current.map((div: any) => div?.getBoundingClientRect()));
		};

		const debouncedHandleResize = debounce(() => {
			setWindowDimensions({
				width: window.innerWidth,
				height: window.innerHeight,
			});
			updateCoordinates();
		}, 250);

		window.addEventListener("resize", debouncedHandleResize);

		updateCoordinates();

		return () => {
			window.removeEventListener("resize", debouncedHandleResize);
		};
	}, []);

	useEffect(() => {
		setDivCoords(divRefs.current.map((div: any) => div?.getBoundingClientRect()));
		setDetailsCoords(detailsRefs.current.map((div: any) => div?.getBoundingClientRect()));
		setFeedbackCoords(feedbackRefs.current.map((div: any) => div?.getBoundingClientRect()));
	}, [windowDimensions]);

	const [divCoords, setDivCoords] = useState([]);
	const [detailsCoords, setDetailsCoords] = useState([]);
	const [activeFeedbackIndex, setActiveFeedbackIndex] = useState<number>(-1);

	const isCursorOverFeedbackIcon = (clientX: number, clientY: number) => {
		return feedbackCoords.some((coords: any) => {
			return clientX >= coords.left && clientX <= coords.right && clientY >= coords.top && clientY <= coords.bottom;
		});
	};

	const focusOnShift = (event: any, isClick?: boolean) => {
		if (drawerOpen) return;
		const { clientX, clientY } = event;
		let isMouseOverBox = false;
		let yInPlace = false;
		if (divCoords?.length) {
			const coords: any = divCoords[0];
			if (clientY >= coords.top && clientY <= coords.bottom) {
				yInPlace = true;
			}
		}
		if (yInPlace) {
			divCoords.forEach((coords: any, index) => {
				if (clientX >= coords.left && clientX <= coords.right) {
					handleMouseEnter(index);
					isMouseOverBox = true;
				}
			});
		}
		if (!isMouseOverBox) {
			handleMouseLeave();
		}
		if (yInPlace && (isCursorOverDetailDiv(clientX, clientY) || isCursorOverFeedbackIcon(clientX, clientY))) {
			setCursorStyle("pointer");
		} else {
			setCursorStyle("default");
		}
		if (drawerOpen) {
			setCursorStyle("default");
		}
	};

	const handleClick = (event: any) => {
		const { clientX, clientY } = event;

		let feedbackClicked = false;
		feedbackCoords.forEach((coords: any, index) => {
			if (activeFeedbackIndex < 0 && clientX >= coords.left && clientX <= coords.right && clientY >= coords.top && clientY <= coords.bottom) {
				handleFeedbackIconClick(index);
				trackEvent("shift insights - Feedback icon clicked", {
					shiftNo: getSpecificShiftNo(index),
				});
				feedbackClicked = true;
			}
		});
		if (feedbackClicked) return;

		if (isMobile) {
			detailsCoords.forEach((coords: any, index) => {
				if (clientX >= coords.left && clientX <= coords.right && clientY >= coords.top && clientY <= coords.bottom) {
					if (detailsRefs.current[index]?.style.opacity === "1" && activeFeedbackIndex < 0) {
						handleMouseClick(event, index);
					}
					trackEvent("shift insights - Schedule summary open", {
						shiftNo: getSpecificShiftNo(index),
					});
				}
			});
		} else {
			detailsCoords.forEach((coords: any, index) => {
				if (clientX >= coords.left && clientX <= coords.right && clientY >= coords.top && clientY <= coords.bottom) {
					if (detailsRefs.current[index]?.style.opacity === "1" && activeFeedbackIndex < 0) {
						handleMouseClick(event, index);
					}
				}
			});
		}
	};

	useEffect(() => {
		window.addEventListener("click", handleClick);
		return () => window.removeEventListener("click", handleClick);
	}, [detailsCoords, feedbackCoords]);

	const handleFeedbackIconClick = (index: number) => {
		setActiveFeedbackIndex(index === feedbackIndex ? -1 : index);
		feedbackIndex = index === feedbackIndex ? -1 : index;
	};

	const handleMouseClick = (e: any, index: number) => {
		if (feedbackIndex >= 0) return;
		trackEvent("shift insights - Schedule summary open", {
			shiftNo: getSpecificShiftNo(index),
		});
		setCursorStyle("default");
		if (drawerOpen && !e.target.closest(".MuiDrawer-paper")) {
			setDrawerOpen(false);
			return;
		}
		if (drawerOpen) {
			return;
		}
		setHoveredIndex(index);
		setClickedShiftNo(shiftNo);
		setClickedDayNo(get_day_index(index));
		setDrawerOpen(true);
		e.stopPropagation();
	};

	const handleMouseMove = useCallback(
		(event: any) => {
			focusOnShift(event);
		},
		// chosenDepartment/isIcuTeleDemo are read via focusOnShift -> handleMouseEnter, which now
		// computes the Holland area splits; without them a department switch keeps the old closure.
		[drawerOpen, divCoords, detailsCoords, weekIndex, hospitalData, showFeedback, chosenDepartment, isIcuTeleDemo],
	);

	const handleMouseEnter = (index: number) => {
		setHoveredIndex(index);
		const day = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[get_day_index(index)];
		setDateWeekDayLabel(day?.date);
		setDailyCensusLow(day?.census_low);
		setDailyCensusHigh(day?.census_high);
		setScore(Number(day?.score).toFixed(1));
		setAdmissionsLow(day?.shiftEvents?.numberAdmissionsLow);
		setAdmissionsHigh(day?.shiftEvents?.numberAdmissionsHigh);
		setDischargesLow(day?.shiftEvents?.numberDischargesLow);
		setDischargesHigh(day?.shiftEvents?.numberDischargesHigh);
		setIntermediateLow(day?.shiftEvents?.numberIntermediateLow);
		setIntermediateHigh(day?.shiftEvents?.numberIntermediateHigh);
		setShiftNo(shiftNo);
		setRnRecommendation(day?.staffNo ?? 0);
		setCensusColor(getColorByDifficultyName(day?.censusDifficulty));
		setScoreColor(getColorByDifficultyName(day?.patientDifficulty));
		setDischargesColor(getColorByDifficultyName(day?.shiftEvents?.difficultyDischarges));
		setAdmissionsColor(getColorByDifficultyName(day?.shiftEvents?.difficultyAdmissions));

		// ICU/Tele demo only. `day` can be undefined on a stale index (hence the `day?.` above),
		// and sidebarAreaSplits would throw on it, so skip rather than call it.
		setAreaSplits(
			!isIcuTeleDemo || !day
				? null
				: sidebarAreaSplits({
						day,
						shiftType: shifts.type,
						locations: chosenDepartment.locations ?? [],
						statsMode: chosenDepartment.statsMode,
					}),
		);

		const hoveredShiftNoFromAlgo = getSpecificShiftNo(index);
		if (hoveredShiftNo !== hoveredShiftNoFromAlgo) {
			sethoveredShiftNo(hoveredShiftNoFromAlgo);
		}
	};

	const handleMouseLeave = () => {
		setHoveredIndex(-1);
	};

	const isCursorOverDetailDiv = (clientX: number, clientY: number) => {
		return detailsCoords.some((coords: any) => {
			return clientX >= coords.left && clientX <= coords.right && clientY >= coords.top && clientY <= coords.bottom;
		});
	};

	useEffect(() => {
		if (cursorStyle === "pointer") {
			document.body.style.cursor = "pointer";
		} else if (cursorStyle === "default") {
			document.body.style.cursor = "default";
		}
	}, [cursorStyle]);

	useEffect(() => {
		window.addEventListener("click", handleClick);
		return () => window.removeEventListener("click", handleClick);
	}, [detailsCoords]);

	const customPointStyle = (context: any) => {
		const index = context.dataIndex;
		const shift = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[index];
		const encodedData = drawShiftCircleImsSrc(shift, 14, 0, isDemoChart);

		const img = new Image();
		img.src = encodedData;
		return img;
	};
	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		onHover: (event: any, chartElement: string | any[]) => {
			const x = event.native.target.getBoundingClientRect().x + chartElement[0]?.element.x;
			const y = event.native.target.getBoundingClientRect().y + chartElement[0]?.element.y;

			if (chartElement.length > 0 && x && y) {
				const index =
					i18n.dir() === "rtl" ? hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days.length - 1 - chartElement[0].index : chartElement[0].index;
				if (chartRef.current && tooltipData.index !== index) {
					setTooltipData({
						visible: true,
						x: x,
						y: y,
						index: index,
					});
				}
			}
		},
		scales: {
			x: {
				reverse: i18n.dir() === "rtl",
				position: "top",
				grid: {
					display: false,
				},
				border: {
					display: false,
				},
				ticks: {
					font: {
						size: windowWidth * 0.009,
						weight: "bold",
						color: "var(--inhouse-primary-light)",
					},
				},
				offset: true,
				display: false,
			},
			y: {
				suggestedMax: maxVal + 1,
				suggestedMin: minVal - 1,
				ticks: {
					stepSize: 1,
					display: false,
				},
				border: {
					display: false,
				},
				grid: {
					color: "rgba(133, 134, 141, 0.15)",
					drawBorder: false,
				},
			},
		},

		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				enabled: false,
			},
		},
	};

	useEffect(() => {
		if (shiftNo !== 0) return;

		const day = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[0];
		setDateWeekDayLabel(day?.date);
		setDailyCensusLow(day?.census_low);
		setDailyCensusHigh(day?.census_high);
		setScore(Number(day?.score).toFixed(1));
		setAdmissionsLow(day?.shiftEvents?.numberAdmissionsLow);
		setAdmissionsHigh(day?.shiftEvents?.numberAdmissionsHigh);
		setDischargesLow(day?.shiftEvents?.numberDischargesLow);
		setDischargesHigh(day?.shiftEvents?.numberDischargesHigh);
		setIntermediateLow(day?.shiftEvents?.numberIntermediateLow);
		setIntermediateHigh(day?.shiftEvents?.numberIntermediateHigh);
		setCensusColor(getColorByDifficultyName(day?.censusDifficulty));
		setScoreColor(getColorByDifficultyName(day?.patientDifficulty));
		setDischargesColor(getColorByDifficultyName(day?.shiftEvents?.difficultyDischarges));
		setAdmissionsColor(getColorByDifficultyName(day?.shiftEvents?.difficultyAdmissions));
	}, [hospitalData, weekIndex]);

	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	useEffect(() => {
		setIsMobile(window.innerWidth < 768);
	}, [window.innerWidth]);

	useEffect(() => {
		const debouncedMouseMove = handleMouseMove;

		window.addEventListener("mousemove", debouncedMouseMove);
		return () => window.removeEventListener("mousemove", debouncedMouseMove);
	}, [handleMouseMove]);

	const chartData = {
		labels: hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days
			.map((e: any) => DateTime.fromISO(e.date).setLocale(i18n.language))
			.map((m: any) => `${m.toFormat("cccc")}-${m.toFormat("D")}`),
		datasets: [
			{
				data: graphData,
				backgroundColor: "transparent",
				zIndex: 10,
				pointRadius: 10,
				pointHitRadius: 10,
				borderColor: "#85868D",
				pointStyle: customPointStyle,
			},
		],
	};

	const get_day_index = (index: number) => {
		return i18n.dir() === "rtl" ? hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days.length - 1 - index : index;
	};

	useEffect(() => {
		if (hoveredShiftNo > 0) {
			const daysofTheweek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
			trackEvent("shift insights - Shift Hovered", {
				dayOfTheWeek: daysofTheweek[hoveredShiftNo - 1],
				shiftType: shiftNo === 0 ? "Morning" : "Evening",
				chosenDepartment: chosenDepartment.display_name,
			});
		}
	}, [hoveredShiftNo]);

	useEffect(() => {
		setCommentText("");
	}, [activeFeedbackIndex]);

	const getSpecificShiftNo = (index: number) => {
		const selectedDate = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.days[index]?.date;
		const timeType = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.type;
		return getDayTimeNumber(selectedDate, timeType, hospitalData, weekIndex);
	};

	const days = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days;
	const shifts = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo];
	useEffect(() => {
		let days = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days;
		days.sort((a: any, b: any) => {
			return new Date(a.date).getTime() - new Date(b.date).getTime();
		});
		if (i18n.dir() === "rtl" && isMobile) {
			days = days.reverse();
		}
		setDaysData(days);
	}, [isMobile, weekIndex]);

	if (undefined === days) return <></>;

	function handleFeedbackClick(newVal: FeedbackValue, index: number): void {
		setFeedbackVals((prevGrid: FeedbackValue[][]) => {
			const newGrid = [...prevGrid];
			newGrid[shiftNo] = [...newGrid[shiftNo]];
			newGrid[shiftNo][index] = newVal;
			return newGrid;
		});
	}

	const sendFeedback = (index: number) => {
		const dayIndex = get_day_index(index);
		let shiftType = "Morning";
		if (hospitalData.weeks[weekIndex].weeklyShifts.length === 3 && shiftNo === 1) {
			shiftType = "Evening";
		} else if (hospitalData.weeks[weekIndex].weeklyShifts.length === 3 && shiftNo === 2) {
			shiftType = "Night";
		}
		if (hospitalData.weeks[weekIndex].weeklyShifts.length === 2 && shiftNo === 1) {
			shiftType = "Night";
		}
		const shiftDate = hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayIndex].date;
		axiosInstance
			.post(`/departments/feedback?department_id=${chosenDepartment.department_id}`, {
				shift_date: shiftDate,
				shift_type: shiftType,
				alignment: feedbackVals[shiftNo][index],
				comment: commentText,
				feedback_type: "single_shift",
			})
			.then(() => {
				showSnackbar(t("feedback.success"), "success");
			})
			.catch((error) => {
				showErrorSnackbar(
					t("feedback.error"),
					{
						action: "error submitting feedback",
						shift_date: shiftDate,
						shift_type: shiftType,
					},
					error,
				);
			});
	};

	const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
		display: "flex",
		width: "100%",
		backgroundColor: theme.palette.background.paper,
		borderRadius: theme.shape.borderRadius,
		border: `1px solid ${theme.palette.divider}`,
	}));

	const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
		flex: 1,
		padding: theme.spacing(0.5),
		border: "none",
		fontSize: "0.5rem",
		lineHeight: "10px",
		borderRadius: 0,
		fontWeight: "bold",
		textTransform: "none",
		p: 0,
		[theme.breakpoints.up("lg")]: {
			fontSize: "1rem", // Font size for larger screens
			lineHeight: "15px", // Line height for larger screens
		},
		[theme.breakpoints.up("md")]: {
			fontSize: "1rem", // Font size for larger screens
			lineHeight: "15px", // Line height for larger screens
		},
		[theme.breakpoints.up("sm")]: {
			fontSize: "0.8rem", // Font size for larger screens
			lineHeight: "15px", // Line height for larger screens
		},
		"&.Mui-selected": {
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.primary.contrastText,
			"&:hover": {
				backgroundColor: theme.palette.primary.dark,
			},
		},
	}));

	return (
		<div
			id={`${windowWidth}.${shiftNo}`}
			style={{
				width: "100%",
				maxWidth: "calc(95vw - 245px)",
				minWidth: isMobile ? "200vw" : "calc(36vw - 2px)",
				position: "relative",
				height: "100%",
				zIndex: 100,
			}}
		>
			{[...Array(hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days.length)].map((_, index) => (
				<Card
					elevation={0}
					sx={{
						boxShadow: index === hoveredIndex ? "0px 5px 15px rgba(0, 0, 0, 0.2)" : "0px 2px 7px rgba(0, 0, 0, 0.2)",
					}}
					key={index}
					style={{
						width: `${100 / hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days.length - 1.2}%`,
						cursor: "pointer",
						height: "calc(100% - 18px)",
						position: "absolute",
						backgroundColor:
							feedbackVals?.length && (feedbackVals[shiftNo][index] === "Too Low" || feedbackVals[shiftNo][index] === "Too High") && showFeedback
								? // Demo mirrors the chart's node language: turquoise wash for Too Low, red for Too High.
									// Non-demo keeps the original single red wash for both.
									isDemoChart && feedbackVals[shiftNo][index] === "Too Low"
									? HOSPITAL_DEMO.FEEDBACK_LOW
									: HOSPITAL_DEMO.FEEDBACK_HIGH
								: index === hoveredIndex && !isMobile
									? "rgba(166, 153, 218, 0.2)"
									: "rgba(128, 128, 128, 0.1)",
						borderRadius: `${RADIUS.CARD}px`,
						borderColor: "white",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						borderWidth: 10,
						zIndex: 10,
						left: `${(index * 100) / hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days.length}%`,
						top: "9px",
					}}
					onClick={(e) => {
						if (isMobile) {
							handleMouseClick(e, get_day_index(index));
						}
					}}
					onMouseEnter={() => setCursorStyle("pointer")}
					ref={(el) => {
						divRefs.current[index] = el;
					}}
				>
					<div
						style={{
							position: "absolute",
							top: 0,
							right: 0,
							left: 0,
							padding: "10px 10px",
							width: "100%",
						}}
					>
						{!showFeedback && (
							<div
								ref={(el) => {
									feedbackRefs.current[index] = el;
								}}
							>
								{activeFeedbackIndex < 0 || activeFeedbackIndex !== index ? (
									<FeedbackIcon
										sx={{
											color: "rgba(128, 128, 128, 0.5)",
											opacity: index === hoveredIndex ? 1 : 0,
										}}
									/>
								) : (
									<Grid
										container
										direction="row"
										sx={{
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<SendIcon
											onClick={() => {
												sendFeedback(index);
											}}
											color="primary"
										/>

										<CloseIcon
											sx={{
												color: "rgba(128, 128, 128, 0.5)",
											}}
										/>
									</Grid>
								)}
							</div>
						)}

						{showFeedback && (
							<Typography
								sx={{
									textAlign: "center",
								}}
							>
								{t(feedbackVals[shiftNo][index])}
							</Typography>
						)}
						<Grid
							container
							direction="row"
							sx={{
								justifyContent: "flex-end",
								alignItems: "flex-start",
							}}
						>
							{isMobile ? (
								<>
									{(daysData[index]?.shiftEvents?.intermediate || 0) <= 0.5 && chosenDepartment?.has_intermediate && (
										<img
											alt="low-certainty-icon"
											style={{
												width: "30px",
												height: "30px",
												opacity: 0.7,
											}}
											src={(daysData[index]?.shiftEvents?.intermediate ?? 0) > 0.5 ? "/emergency-room-open.png" : "/closed-sign.png"}
										/>
									)}
								</>
							) : (
								<>
									{(days[get_day_index(index)]?.shiftEvents?.intermediate || 0) <= 0.5 && chosenDepartment?.has_intermediate && (
										<img
											alt="low-certainty-icon"
											style={{
												width: "30px",
												height: "30px",
												opacity: 0.7,
											}}
											src={(days[get_day_index(index)]?.shiftEvents?.intermediate ?? 0) > 0.5 ? "/emergency-room-open.png" : "/closed-sign.png"}
										/>
									)}
								</>
							)}
						</Grid>
					</div>
					{!isMobile && !showFeedback && (
						<div
							style={{
								display: "flex",
								cursor: "pointer",
								padding: 5,
								opacity: index === hoveredIndex ? 1 : 0,
								marginBottom: "5%",
								borderRadius: RADIUS.CARD,
								alignSelf: "flex-end",
								alignItems: "center",
								justifyContent: "center",
								background: "linear-gradient(191.99deg, var(--inhouse-primary-dark) -67.33%, var(--inhouse-primary-light) 199.72%)",
								color: "white",
								fontSize: "1rem",
							}}
							onMouseEnter={() => {
								setCursorStyle("pointer");
							}}
							ref={(el) => {
								detailsRefs.current[index] = el;
							}}
						>
							{t("Details")}
						</div>
					)}

					{days[index]?.changeAlert && (
						<div style={{ position: "absolute", bottom: "-10px", left: "0px" }}>
							<Dot />
						</div>
					)}
					{isMobile && (
						<div
							style={{
								width: "100%",
								position: "absolute",
								left: 0,
								bottom: 0,
								height: "100%",
							}}
						>
							<Grid
								container
								sx={{
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									width: "100%",
									textAlign: "center",
									height: "100%",
								}}
							>
								<div
									style={{
										// make background color of the day of the week
										backgroundColor:
											daysData[index]?.staffDifficulty === "high"
												? // Demo: red high node; others keep the original orange.
													isDemoChart
													? HOSPITAL_DEMO.NODE_HIGH
													: "#FFA96A"
												: daysData[index]?.staffDifficulty === "low"
													? // Demo: blue low node; others keep the original blue.
														isDemoChart
														? HOSPITAL_DEMO.NODE_LOW
														: "#5EB8F9"
													: isDemoChart
														? HOSPITAL_DEMO.NODE_MEDIUM
														: "var(--inhouse-primary-light)",
										borderRadius: "50%",
										padding: "10px",
										width: "45px",
										color: "white",
										fontWeight: "bold",
										marginBottom: "10px",
										// Hospital demo uses DM Sans for the node number to match the page.
										...(isDemoChart && { fontFamily: DEMO_FONT_FAMILY }),
									}}
								>
									{daysData[index]?.staffNo + (daysData[index]?.isPlusActive ? "+" : daysData[index]?.isMinusActive ? "-" : "")}
								</div>

								<div
									style={{
										color: getColorByDifficultyName(daysData[index]?.censusDifficulty),
									}}
								>
									{presentRangeScore(daysData[index]?.census_low, daysData[index]?.census_high)} <br />{" "}
									<span
										style={{
											fontSize: "12px",
											color: getColorByDifficultyName(daysData[index].patientDifficulty),
										}}
									>
										<span style={{ fontWeight: 400 }}>{t("Patient Score")} </span>
										{daysData[index].score.toFixed(1)}
									</span>
								</div>

								<div
									style={{
										fontSize: "12px",
										color: getColorByDifficultyName(daysData[index]?.shiftEvents?.difficultyAdmissions),
									}}
								>
									<span style={{ fontWeight: 400 }}>{t("Admissions")}</span>{" "}
									{presentRangeScore(daysData[index]?.shiftEvents?.numberAdmissionsLow, daysData[index]?.shiftEvents?.numberAdmissionsHigh)}
								</div>

								<div
									style={{
										fontSize: "12px",
										color: getColorByDifficultyName(daysData[index]?.shiftEvents?.difficultyDischarges),
									}}
								>
									<span style={{ fontWeight: 400 }}>{t("Discharges")}</span>{" "}
									{presentRangeScore(daysData[index]?.shiftEvents?.numberDischargesLow, daysData[index]?.shiftEvents?.numberDischargesHigh)}
								</div>

								{chosenDepartment?.has_intermediate && (
									<div style={{ fontSize: "12px", color: "var(--inhouse-primary-light)" }}>
										<span style={{ fontWeight: 400 }}>{t("Intermediate")}</span>{" "}
										{presentRangeScore(daysData[index]?.shiftEvents?.numberIntermediateLow, daysData[index]?.shiftEvents?.numberIntermediateHigh)}
									</div>
								)}
							</Grid>
						</div>
					)}
					{((showFeedback && index === hoveredIndex) || index === activeFeedbackIndex) && (
						<div
							style={{
								backgroundColor: "white",
								position: "absolute",
								zIndex: 2147483647,
								bottom: 0,
								left: 0,
								right: 0,
								margin: "auto",
								width: "100%",
								padding: "2px",
								direction: i18n.dir(),
							}}
						>
							<StyledToggleButtonGroup
								color="primary"
								value={feedbackVals[shiftNo][index]}
								exclusive
								onChange={(event, newValue) => {
									if (newValue !== null) {
										handleFeedbackClick(newValue, index);
									}
								}}
								aria-label="prediction accuracy"
								fullWidth
								sx={{
									"& .MuiToggleButton-root": {
										"&:not(:last-of-type)": {
											borderRight: i18n.dir() === "ltr" ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
											borderLeft: i18n.dir() === "rtl" ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
										},
									},
									"& .MuiToggleButton-root:first-of-type": {
										borderTopLeftRadius: i18n.dir() === "ltr" ? 4 : 0,
										borderBottomLeftRadius: i18n.dir() === "ltr" ? 4 : 0,
										borderTopRightRadius: i18n.dir() === "rtl" ? 4 : 0,
										borderBottomRightRadius: i18n.dir() === "rtl" ? 4 : 0,
									},
									"& .MuiToggleButton-root:last-of-type": {
										borderTopRightRadius: i18n.dir() === "ltr" ? 4 : 0,
										borderBottomRightRadius: i18n.dir() === "ltr" ? 4 : 0,
										borderTopLeftRadius: i18n.dir() === "rtl" ? 4 : 0,
										borderBottomLeftRadius: i18n.dir() === "rtl" ? 4 : 0,
									},
								}}
							>
								<StyledToggleButton value="Too Low" aria-label="too low">
									{t("Too Low")}
								</StyledToggleButton>
								<StyledToggleButton value="Precisely" aria-label="precisely">
									{t("Precisely")}
								</StyledToggleButton>
								<StyledToggleButton value="Too High" aria-label="too high">
									{t("Too High")}
								</StyledToggleButton>
							</StyledToggleButtonGroup>
							{!showFeedback && (
								<div>
									<TextField
										label={t("Comment")}
										size="small"
										sx={{
											width: "100%",
											marginTop: 0,
											"& .MuiInputBase-input": {
												fontSize: 10,
											},
										}}
										fullWidth
										value={commentText}
										onChange={(e) => setCommentText(e.target.value)}
									/>
								</div>
							)}
						</div>
					)}
				</Card>
			))}
			{!isMobile &&
				(showFeedback || (activeFeedbackIndex && activeFeedbackIndex > -1) ? (
					<div
						ref={chartRef}
						style={{
							width: "100%",
							maxWidth: "100%",
							height: "100%",
							zIndex: 0,
							padding: "17px 0px 20px 0px",
							position: "relative",
							pointerEvents: "none", // Add this line
						}}
					>
						<Line
							style={{
								zIndex: 0,
								position: "relative",
								width: "100%",
								padding: 2,
							}}
							data={chartData}
							options={chartOptions as any}
						/>
					</div>
				) : (
					<div
						ref={chartRef}
						style={{
							width: "100%",
							maxWidth: "100%",
							height: "100%",
							zIndex: 1,
							padding: "17px 0px 20px 0px",
						}}
					>
						<Line
							style={{
								zIndex: 10,
								position: "relative",
								width: "100%",
								padding: 2,
							}}
							data={chartData}
							options={chartOptions as any}
						/>
					</div>
				))}
			{tooltipData.visible && <ShiftTooltip tooltipData={tooltipData} shifts={shifts} days={days} />}
		</div>
	);
};

export const WhitelTooltip = styled(({ className, ...props }: TooltipProps) => (
	<MuiTooltip arrow enterTouchDelay={0} leaveTouchDelay={1500} {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "#FFFFFF",
		color: "rgba(0, 0, 0, 0.87)",
		padding: "15px",
		fontSize: theme.typography.pxToRem(12),
	},
	[`& .${tooltipClasses.arrow}`]: {
		color: "#FFFFFF",
	},
}));

export default memo(LineChart);

export const ShiftTooltip = ({ tooltipData, shifts, days }: any) => {
	const { t, i18n } = useTranslation();

	return (
		<WhitelTooltip
			onOpen={() => {
				trackEvent("shift insights - Tooltip Opened");
			}}
			placement={i18n.dir() === "rtl" ? "left" : "right"}
			title={<ShiftDifficultyIndicator shifts={shifts} tooltipData={tooltipData} />}
		>
			<div
				style={{
					position: "fixed",
					left: tooltipData.x - 15,
					top: tooltipData.y - 15,
					zIndex: 20,
					opacity: 0,
					cursor: "pointer",
					width: "30px",
					height: "30px",
					borderRadius: "50%",
				}}
			/>
		</WhitelTooltip>
	);
};

type Difficulty = "low" | "medium" | "high";

export const StatusIndicator = ({ difficulty }: { difficulty: Difficulty }) => {
	const { t } = useTranslation();
	const { isHospitalView } = useHospitalData();
	// Only hospital demos (Hospital type + inhouse2).
	const isDemoChart = isHospitalView;
	const iconMap: Record<Difficulty, string> = {
		low: "/low-difficulty.png",
		medium: "/medium-difficulty.png",
		high: "/high-difficulty.png",
	};
	const textMap: Record<Difficulty, string> = {
		low: t("Low.level"),
		medium: t("Medium.level"),
		high: t("High.level"),
	};
	// Demo dot colors match the redesigned chart nodes; others use the original PNG icons.
	const demoDotColor: Record<Difficulty, string> = { low: HOSPITAL_DEMO.NODE_LOW, medium: HOSPITAL_DEMO.NODE_MEDIUM, high: HOSPITAL_DEMO.NODE_HIGH };

	return (
		<>
			<Grid
				container
				direction="row"
				sx={{
					justifyContent: "flex-start",
					alignItems: "center",
					flexWrap: "nowrap",
				}}
			>
				{isDemoChart ? (
					<div style={{ height: "18px", width: "18px", borderRadius: "50%", backgroundColor: demoDotColor[difficulty] }} />
				) : (
					<img alt={`${difficulty}-difficulty-icon`} src={iconMap[difficulty]} style={{ height: "30px", width: "30px" }} />
				)}
				<div style={{ marginLeft: "10px" }}>
					{textMap[difficulty]} {t("tooltip.status")}
				</div>
			</Grid>
		</>
	);
};

export const ShiftDifficultyIndicator = ({ shifts, tooltipData }: any) => {
	const { t, i18n } = useTranslation();
	const { chosenDepartment, isIcuTeleDemo } = useHospitalData();
	const index = i18n.dir() === "rtl" ? shifts.days.length - 1 - tooltipData.index : tooltipData.index;
	const day = shifts.days[index];

	// The wire carries one department-wide recommendation per shift; the per-area split is
	// recomputed from the ladder the Staffing grid reads, so the two always agree.
	// Holland-only: the other hospital demos have no meaningful area split (see isIcuTeleDemo).
	const breakdown = useMemo(
		() =>
			!isIcuTeleDemo || !day
				? []
				: recommendationBreakdown({
						locations: chosenDepartment.locations ?? [],
						byDimensions: day.census_grouping?.by_dimensions ?? {},
						totalCensus: day.census_high ?? 0,
						statsMode: chosenDepartment.statsMode,
						shiftType: shifts.type,
						dayAbbr: day.date?.toFormat("ccc").toLowerCase() ?? "",
						score: day.score ?? 1,
					}),
		[isIcuTeleDemo, chosenDepartment.locations, chosenDepartment.statsMode, day, shifts.type],
	);

	// A stale tooltip index can point outside the week (day/week switch mid-hover). Render nothing
	// rather than destructuring undefined and taking the chart subtree down with it.
	if (!day) return null;
	const { certainty, staffDifficulty } = day;

	return (
		<div style={{ fontSize: "14px" }}>
			{certainty < 1 && (
				<div>
					<Grid
						container
						direction="row"
						sx={{
							justifyContent: "flex-start",
							alignItems: "center",
							flexWrap: "nowrap",
						}}
					>
						<img alt="low-certainty-icon" src="/dashed.png" style={{ height: "30px", width: "30px", opacity: 0.3 }} />
						<div style={{ marginLeft: "10px" }}>{t("low.certainty")}</div>
					</Grid>
					<Divider sx={{ margin: "10px 0px" }} />
				</div>
			)}
			<StatusIndicator difficulty={staffDifficulty} />
			{breakdown.length > 1 && (
				<>
					<Divider sx={{ margin: "10px 0px" }} />
					<Box sx={{ display: "flex", justifyContent: "space-between", gap: 3, mb: 0.5 }}>
						<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS }}>{t("shift.insights.total.rn.recommendation")}</Typography>
						<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700 }}>{t("shift.insights.rn.count", { count: day.staffNo })}</Typography>
					</Box>
					{breakdown.map((part, partIndex) => (
						<Box key={part.unit} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 3 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Box
									sx={{
										width: 8,
										height: 8,
										borderRadius: "50%",
										flexShrink: 0,
										// Colors are ordered by row, matching the sidebar's breakdown.
										backgroundColor: HOSPITAL_DEMO.BREAKDOWN_AREA_COLORS[partIndex % HOSPITAL_DEMO.BREAKDOWN_AREA_COLORS.length].dot,
									}}
								/>
								<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: "text.secondary" }}>{t("shift.insights.area.rn.need", { area: part.unit })}</Typography>
							</Box>
							<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600 }}>{t("shift.insights.rn.count", { count: part.nurses })}</Typography>
						</Box>
					))}
				</>
			)}
		</div>
	);
};
